import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { eventLogService } from '../services/eventLogService';
import { useToastContext } from '../contexts/ToastContext';

function getMutationErrorMessage(error, fallback) {
  const data = error?.response?.data;
  if (data?.message && typeof data.message === 'string') {
    return data.message;
  }
  if (data?.errors && typeof data.errors === 'object') {
    const firstKey = Object.keys(data.errors)[0];
    const val = firstKey ? data.errors[firstKey] : null;
    if (Array.isArray(val) && val[0]) return String(val[0]);
    if (typeof val === 'string') return val;
  }
  return fallback;
}

export const useEventLogs = (bookingId) => {
  const queryClient = useQueryClient();
  const { showToast } = useToastContext();

  // Fetch event logs
  const { data, isLoading, error } = useQuery({
    queryKey: ['eventLogs', bookingId],
    queryFn: () => eventLogService.getEventLogs(bookingId),
    enabled: !!bookingId,
    select: (response) => response?.data || [],
  });

  // Start event mutation (no onSuccess/onError here - handled in wrapper)
  const startEventMutation = useMutation({
    mutationFn: (data) => eventLogService.startEvent(bookingId, data),
  });

  // End event mutation (no onSuccess/onError here - handled in wrapper)
  const endEventMutation = useMutation({
    mutationFn: (data) => eventLogService.endEvent(bookingId, data),
  });

  return {
    eventLogs: data || [],
    isLoading,
    error,
    startEvent: (data, options = {}) => {
      const customOnSuccess = options?.onSuccess;
      const customOnError = options?.onError;
      
      startEventMutation.mutate(data, {
        onSuccess: (response) => {
          queryClient.invalidateQueries(['eventLogs', bookingId]);
          queryClient.invalidateQueries(['vendorBooking', bookingId]);
          queryClient.invalidateQueries(['booking', bookingId]);
          showToast('Event started successfully', 'success');
          // Call the custom onSuccess callback if provided
          if (customOnSuccess) {
            customOnSuccess(response);
          }
        },
        onError: (error) => {
          showToast(getMutationErrorMessage(error, 'Failed to start event'), 'error');
          // Call the custom onError callback if provided
          if (customOnError) {
            customOnError(error);
          }
        },
      });
    },
    endEvent: (data, options = {}) => {
      const customOnSuccess = options?.onSuccess;
      const customOnError = options?.onError;
      
      endEventMutation.mutate(data, {
        onSuccess: (response) => {
          // Invalidate queries first
          queryClient.invalidateQueries(['eventLogs', bookingId]);
          queryClient.invalidateQueries(['vendorBooking', bookingId]);
          queryClient.invalidateQueries(['booking', bookingId]);
          
          // Show toast
          showToast('Event ended successfully. Booking status updated to completed.', 'success');
          
          // Call the custom onSuccess callback if provided
          if (customOnSuccess) {
            customOnSuccess(response);
          }
        },
        onError: (error) => {
          showToast(getMutationErrorMessage(error, 'Failed to end event'), 'error');
          // Call the custom onError callback if provided
          if (customOnError) {
            customOnError(error);
          }
        },
      });
    },
    isStarting: startEventMutation.isPending,
    isEnding: endEventMutation.isPending,
  };
};

